import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { SlidersHorizontal } from 'lucide-react'

export interface ContextMenuPosition {
  x: number
  y: number
}

export interface ContextMenuItem {
  id: string
  label: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  variant?: 'default' | 'danger'
}

export interface CalculateMenuPositionOptions {
  x: number
  y: number
  menuWidth?: number
  menuHeight?: number
  windowWidth?: number
  windowHeight?: number
  padding?: number
}

/**
 * Pure helper: Calculate menu position with boundary flipping when near edge.
 */
export function calculateMenuPosition(options: CalculateMenuPositionOptions): {
  left: number
  top: number
} {
  const {
    x,
    y,
    menuWidth = 180,
    menuHeight = 120,
    windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1024,
    windowHeight = typeof window !== 'undefined' ? window.innerHeight : 768,
    padding = 8,
  } = options

  let left = x
  if (x + menuWidth > windowWidth - padding) {
    left = Math.max(padding, x - menuWidth)
  } else {
    left = Math.max(padding, left)
  }

  let top = y
  if (y + menuHeight > windowHeight - padding) {
    top = Math.max(padding, y - menuHeight)
  } else {
    top = Math.max(padding, top)
  }

  return { left, top }
}

/**
 * Pure helper: Compute next item index for keyboard arrow navigation.
 */
export function getNextMenuFocusIndex(
  currentIndex: number,
  itemsCount: number,
  action: 'next' | 'prev' | 'first' | 'last',
): number {
  if (itemsCount <= 0) return -1
  switch (action) {
    case 'next':
      return currentIndex < 0 ? 0 : (currentIndex + 1) % itemsCount
    case 'prev':
      return currentIndex < 0 ? itemsCount - 1 : (currentIndex - 1 + itemsCount) % itemsCount
    case 'first':
      return 0
    case 'last':
      return itemsCount - 1
    default:
      return 0
  }
}

export interface ContextMenuProps {
  isOpen: boolean
  position: ContextMenuPosition
  onClose: () => void
  items: ContextMenuItem[]
}

/**
 * ContextMenu component.
 * Supports:
 * - Right-click / Menu key / Shift+F10
 * - Arrow navigation (Up/Down/Home/End)
 * - Escape / Click outside / Window scroll to dismiss
 * - Viewport boundary flipping
 */
export function ContextMenu({ isOpen, position, onClose, items }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [focusedIndex, setFocusedIndex] = useState(0)

  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(0)
    }
  }, [isOpen])

  // Click outside and scroll listeners
  useEffect(() => {
    if (!isOpen) return

    function handlePointerDown(e: MouseEvent | TouchEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    function handleScroll() {
      onClose()
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    window.addEventListener('scroll', handleScroll, { capture: true, passive: true })
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      window.removeEventListener('scroll', handleScroll, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen || items.length === 0) return null

  const { left, top } = calculateMenuPosition({
    x: position.x,
    y: position.y,
    menuWidth: 200,
    menuHeight: items.length * 36 + 16,
  })

  function handleMenuKeyDown(e: ReactKeyboardEvent) {
    const enabledItems = items.map((it, idx) => ({ ...it, originalIdx: idx })).filter((it) => !it.disabled)
    if (enabledItems.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const currEnabled = enabledItems.findIndex((it) => it.originalIdx === focusedIndex)
      const nextEnabled = getNextMenuFocusIndex(currEnabled, enabledItems.length, 'next')
      setFocusedIndex(enabledItems[nextEnabled].originalIdx)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const currEnabled = enabledItems.findIndex((it) => it.originalIdx === focusedIndex)
      const prevEnabled = getNextMenuFocusIndex(currEnabled, enabledItems.length, 'prev')
      setFocusedIndex(enabledItems[prevEnabled].originalIdx)
    } else if (e.key === 'Home') {
      e.preventDefault()
      setFocusedIndex(enabledItems[0].originalIdx)
    } else if (e.key === 'End') {
      e.preventDefault()
      setFocusedIndex(enabledItems[enabledItems.length - 1].originalIdx)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const item = items[focusedIndex]
      if (item && !item.disabled) {
        item.onClick()
        onClose()
      }
    }
  }

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="Menu thuộc tính"
      tabIndex={-1}
      onKeyDown={handleMenuKeyDown}
      style={{ left: `${left}px`, top: `${top}px` }}
      className="fixed z-50 min-w-[180px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-lg animate-in fade-in zoom-in-95 duration-100"
    >
      {items.map((item, index) => {
        const isFocused = index === focusedIndex
        return (
          <button
            key={item.id}
            role="menuitem"
            tabIndex={item.disabled ? -1 : 0}
            disabled={item.disabled}
            onClick={() => {
              if (!item.disabled) {
                item.onClick()
                onClose()
              }
            }}
            onMouseEnter={() => setFocusedIndex(index)}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
              item.disabled
                ? 'cursor-not-allowed text-slate-400 opacity-60'
                : isFocused
                  ? 'bg-slate-100 text-[var(--color-text)] font-medium'
                  : 'text-[var(--color-text)]'
            } ${item.variant === 'danger' ? 'text-red-600 hover:bg-red-50' : ''}`}
          >
            {item.icon ? <span className="h-4 w-4 shrink-0 text-slate-500">{item.icon}</span> : null}
            <span>{item.label}</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Quick inline button on table row for touch screens and discovery.
 */
export function ContextMenuRowButton({
  onClick,
  title = 'Thuộc tính vật tư',
}: {
  onClick: (e: React.MouseEvent) => void
  title?: string
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick(e)
      }}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200"
    >
      <SlidersHorizontal className="h-3 w-3 text-slate-500" />
      <span>Thuộc tính</span>
    </button>
  )
}
